import React from 'react';
import './GenericDomainLayout.css';
import {
	DataTableProvider,
	MessageHelper,
	FeedBackType,
	useBreakPoint,
	DataTable,
	DataCardList,
	PageLoader,
	Alert,
	ActionStatusHelper,
	DomainType,
	DOMAIN_TYPES,
	useUIFeedback,
	UIFeedback,
	ModalHook,
	useDomainLayout,
} from '@/Shared';
import { DOMAIN_CONFIGS } from '@/Shared/Config/domain-config';
import { PermissionLayout } from '@/Domains';


interface GenericDomainLayoutProps {
	domainType: DomainType;
	inModalHook?: ModalHook;
	extraArgs?: any
}


/**
 * =====================================================
 * GenericDomainLayout
 * =====================================================
 *
 * Generic and reusable layout component for domain list pages.
 *
 * Responsibilities:
 *  - Dynamically resolve the domain configuration and table setup
 *    via DOMAIN_CONFIGS based on the provided domainType.
 *  - Initialize and provide UI feedback hooks (toast, confirm, modal, alerts).
 *  - Handle global loading and error states consistently.
 *  - Render responsive data views (table for desktop, cards for mobile/tablet).
 *
 * Notes:
 *  - This component contains no domain-specific logic. It purely orchestrates
 *    domain-specific configs with generic UI components.
 *  - Sorting of data is applied on mobile/tablet for better UX.
 *
 * Author: Samano CASTRE
 * Date: July 16, 2025
 */
const GenericDomainLayout = <T,>({ 
	domainType,
    inModalHook,
    extraArgs
 }: GenericDomainLayoutProps) => {

	/** Responsive helpers for switching between table and card layouts */
	const { isMobile, isTablet, isDesktop } = useBreakPoint();
	 const {   domainLabelPl } = DOMAIN_CONFIGS[domainType];

	/** UI feedback hooks shared across the layout */
	const uiFeedbackHooks = useUIFeedback();

	const {response, sortedTableConfig} = useDomainLayout(
		domainType,
		uiFeedbackHooks,
		inModalHook,
		extraArgs
	);


	/** Handle global loading and error states */
	const { isLoading, isFailed } = ActionStatusHelper.is(response.status);

	if (isFailed) {
		return (
			<Alert data={{
				message: response.error || MessageHelper.LISTING.ERROR(domainLabelPl),
				type: FeedBackType.ERROR
			}} />
		);
	}


	if (isLoading || !sortedTableConfig) {
		return (
			<PageLoader
				message={MessageHelper.LISTING.LOADING(domainLabelPl)}
			/>
		);
	}
	
	const isPermissionType = domainType === DOMAIN_TYPES.Permission;

	return (
		<div
			className={`GenericDomainLayout ${domainType}`}
			data-testid={domainType ?? 'GenericDomainLayout'}
		>
			<UIFeedback uiFeedbackHooks={uiFeedbackHooks} />

			{isPermissionType && <PermissionLayout tableConfig={sortedTableConfig} />}

			{/* Data rendering layer */}
			{!isPermissionType &&
				<DataTableProvider config={sortedTableConfig}>
					{isDesktop && <DataTable />}
					{(isMobile || isTablet) && <DataCardList />}
				</DataTableProvider>}
		</div>
	);
};

export default GenericDomainLayout;